import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  SlidersHorizontal, Plus, Loader2, Pencil, Archive, ArchiveRestore,
  ChevronUp, ChevronDown, Users, Building2, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Multi-line text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown (single)' },
  { value: 'multiselect', label: 'Dropdown (multi)' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'url', label: 'URL' },
];
const TYPE_LABEL = Object.fromEntries(FIELD_TYPES.map((t) => [t.value, t.label]));
const HAS_OPTIONS = (t) => t === 'select' || t === 'multiselect';

// HubSpot-imported options may be {label, value} objects.
const optLabel = (o) => (o && typeof o === 'object') ? String(o.label ?? o.value ?? '') : String(o ?? '');

const CustomProperties = () => {
  const [objectType, setObjectType] = useState('contact');
  const [props, setProps] = useState([]);
  const [systemProps, setSystemProps] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [reordering, setReordering] = useState(false);

  // create
  const [createOpen, setCreateOpen] = useState(false);
  const [newField, setNewField] = useState({ label: '', field_type: 'text', description: '', options: '' });
  const [creating, setCreating] = useState(false);

  // edit
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', description: '', options: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // archive confirm
  const [archiveTarget, setArchiveTarget] = useState(null);

  // detect-from-records backfill
  const [detecting, setDetecting] = useState(false);

  const fetchProps = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/properties`, {
        params: { object_type: objectType, include_archived: showArchived },
      });
      setProps(res.data.properties || []);
      setSystemProps(res.data.system || []);
    } catch { toast.error('Failed to load properties'); }
    finally { setLoading(false); }
  }, [objectType, showArchived]);

  useEffect(() => { fetchProps(); }, [fetchProps]);

  const detectFromRecords = useCallback(async () => {
    setDetecting(true);
    try {
      await axios.post(`${API}/properties/detect-from-records`);
      toast.info('Scanning your records for custom fields…');
      // poll until the background job finishes
      const poll = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/properties/detect-status`);
          if (data.status === 'completed') {
            clearInterval(poll); setDetecting(false);
            toast.success(data.created > 0 ? `Found & added ${data.created} field${data.created === 1 ? '' : 's'} from your data` : 'No new fields found on your records');
            fetchProps();
          } else if (data.status === 'failed') {
            clearInterval(poll); setDetecting(false);
            toast.error('Could not scan your records');
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (err) {
      setDetecting(false);
      toast.error(err.response?.data?.detail || 'Could not start detection');
    }
  }, [fetchProps]);

  const matches = (p) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (p.label || '').toLowerCase().includes(q) || (p.name || '').toLowerCase().includes(q);
  };
  const active = props.filter((p) => !p.archived && matches(p));
  const archived = props.filter((p) => p.archived && matches(p));
  const system = systemProps.filter(matches);

  const createField = async (e) => {
    e.preventDefault();
    if (!newField.label.trim()) { toast.error('Field name is required'); return; }
    setCreating(true);
    try {
      const payload = {
        object_type: objectType,
        label: newField.label.trim(),
        field_type: newField.field_type,
        description: newField.description.trim() || null,
      };
      if (HAS_OPTIONS(newField.field_type)) {
        payload.options = newField.options.split(',').map((s) => s.trim()).filter(Boolean);
        if (payload.options.length === 0) { toast.error('Add at least one option'); setCreating(false); return; }
      }
      await axios.post(`${API}/properties`, payload);
      toast.success('Property created');
      setNewField({ label: '', field_type: 'text', description: '', options: '' });
      setCreateOpen(false);
      fetchProps();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create property'); }
    finally { setCreating(false); }
  };

  const openEdit = (p) => {
    setEditForm({
      label: p.label || '',
      description: p.description || '',
      options: (p.options || []).map(optLabel).join(', '),
    });
    setEditTarget(p);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.label.trim()) { toast.error('Field name is required'); return; }
    setSavingEdit(true);
    try {
      const payload = { label: editForm.label.trim(), description: editForm.description.trim() || null };
      if (HAS_OPTIONS(editTarget.field_type)) {
        payload.options = editForm.options.split(',').map((s) => s.trim()).filter(Boolean);
        if (payload.options.length === 0) { toast.error('Add at least one option'); setSavingEdit(false); return; }
      }
      await axios.patch(`${API}/properties/${editTarget.id}`, payload);
      toast.success('Property updated');
      setEditTarget(null);
      fetchProps();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update property'); }
    finally { setSavingEdit(false); }
  };

  const doArchive = async () => {
    if (!archiveTarget) return;
    try {
      await axios.delete(`${API}/properties/${archiveTarget.id}`);
      toast.success('Property archived');
      setArchiveTarget(null);
      fetchProps();
    } catch { toast.error('Failed to archive'); }
  };

  const restore = async (p) => {
    try {
      await axios.patch(`${API}/properties/${p.id}`, { archived: false });
      toast.success('Property restored');
      fetchProps();
    } catch { toast.error('Failed to restore'); }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= active.length) return;
    const reordered = [...active];
    const [item] = reordered.splice(index, 1);
    reordered.splice(target, 0, item);
    setProps(showArchived ? [...reordered, ...archived] : reordered);
    setReordering(true);
    try {
      await axios.post(`${API}/properties/reorder`, {
        object_type: objectType,
        ordered_ids: reordered.map((p) => p.id),
      });
    } catch { toast.error('Could not save order'); fetchProps(); }
    finally { setReordering(false); }
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-4xl" data-testid="custom-properties-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg">
              <SlidersHorizontal className="w-5 h-5 text-white" />
            </span>
            Custom Properties
          </h1>
          <p className="text-gray-500 mt-2">
            Define the custom fields on your Contacts and Companies. Edit labels, dropdown options and ordering - they appear on every record and in spreadsheet imports.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={detectFromRecords} disabled={detecting} className="border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="detect-properties-btn">
            {detecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</> : <><Sparkles className="w-4 h-4 mr-2" /> Detect fields from my records</>}
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-white shadow-lg" data-testid="add-property-btn">
            <Plus className="w-4 h-4 mr-2" /> New property
          </Button>
        </div>
      </div>

      {/* Object type toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setObjectType('contact')}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${objectType === 'contact' ? 'bg-violet-50 border-violet-300 text-violet-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          data-testid="tab-contact"
        >
          <Users className="w-4 h-4" /> Contact properties
        </button>
        <button
          onClick={() => setObjectType('company')}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${objectType === 'company' ? 'bg-violet-50 border-violet-300 text-violet-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          data-testid="tab-company"
        >
          <Building2 className="w-4 h-4" /> Company properties
        </button>
        <label className="ml-auto flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-4 h-4 accent-violet-600" data-testid="show-archived-toggle" />
          Show archived
        </label>
      </div>

      {/* Search */}
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${objectType} properties by name…`}
          className="pl-3"
          data-testid="property-search-input"
        />
      </div>

      {/* System properties (read-only) */}
      {system.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="system-properties-list">
          <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-500">System properties</h2>
            <span className="text-xs text-gray-400">{system.length} built-in</span>
          </div>
          <div className="divide-y divide-gray-100">
            {system.map((p) => (
              <div key={p.name} className="px-6 py-3 flex items-center gap-4" data-testid={`system-row-${p.name}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-700">{p.label}</p>
                    <Badge className="bg-gray-100 text-gray-600 border border-gray-200 font-normal">{TYPE_LABEL[p.field_type] || p.field_type}</Badge>
                    <code className="text-xs text-gray-400 break-all">{p.name}</code>
                  </div>
                </div>
                <Badge className="bg-blue-50 text-blue-600 border border-blue-100 font-normal shrink-0">Built-in</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom properties list */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-500">Custom properties</h2>
          {!loading && <span className="text-xs text-gray-400">{active.length} custom</span>}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
        ) : active.length === 0 ? (
          <div className="text-center py-16 px-6">
            <SlidersHorizontal className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No custom {objectType} properties yet.</p>
            <p className="text-gray-400 text-sm mt-1">Already imported data? Detect the custom fields that are already on your records.</p>
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button onClick={detectFromRecords} disabled={detecting} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="empty-detect-properties-btn">
                {detecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</> : <><Sparkles className="w-4 h-4 mr-2" /> Detect fields from my records</>}
              </Button>
              <Button variant="outline" onClick={() => setCreateOpen(true)} data-testid="empty-add-property-btn">
                <Plus className="w-4 h-4 mr-2" /> Create one manually
              </Button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {active.map((p, i) => (
              <div key={p.id} className="px-6 py-4 flex items-center gap-4" data-testid={`property-row-${p.name}`}>
                <div className="flex flex-col -my-1">
                  <button disabled={i === 0 || reordering || !!query} onClick={() => move(i, -1)} className="text-gray-300 hover:text-violet-600 disabled:opacity-30 disabled:hover:text-gray-300" data-testid={`move-up-${p.name}`}>
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button disabled={i === active.length - 1 || reordering || !!query} onClick={() => move(i, 1)} className="text-gray-300 hover:text-violet-600 disabled:opacity-30 disabled:hover:text-gray-300" data-testid={`move-down-${p.name}`}>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900">{p.label}</p>
                    <Badge className="bg-gray-100 text-gray-600 border border-gray-200 font-normal">{TYPE_LABEL[p.field_type] || p.field_type}</Badge>
                    <code className="text-xs text-gray-400 break-all">{p.name}</code>
                  </div>
                  {p.description && <p className="text-sm text-gray-500 mt-0.5 truncate">{p.description}</p>}
                  {HAS_OPTIONS(p.field_type) && (p.options || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {(p.options || []).map((o, idx) => (
                        <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100">{optLabel(o)}</span>
                      ))}
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => openEdit(p)} className="text-gray-400 hover:text-violet-600 hover:bg-violet-50" data-testid={`edit-property-${p.name}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setArchiveTarget(p)} className="text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`archive-property-${p.name}`}>
                  <Archive className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Archived */}
      {showArchived && archived.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="archived-list">
          <div className="px-6 py-3 border-b border-gray-100"><h2 className="text-sm font-semibold text-gray-500">Archived</h2></div>
          <div className="divide-y divide-gray-100">
            {archived.map((p) => (
              <div key={p.id} className="px-6 py-3 flex items-center gap-4" data-testid={`archived-row-${p.name}`}>
                <div className="flex-1 min-w-0">
                  <span className="text-gray-500 line-through">{p.label}</span>
                  <Badge className="ml-2 bg-gray-100 text-gray-500 border border-gray-200 font-normal">{TYPE_LABEL[p.field_type] || p.field_type}</Badge>
                </div>
                <Button variant="ghost" size="sm" onClick={() => restore(p)} className="text-violet-600 hover:bg-violet-50" data-testid={`restore-property-${p.name}`}>
                  <ArchiveRestore className="w-4 h-4 mr-1.5" /> Restore
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white" data-testid="create-property-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-violet-600" /> New {objectType} property</DialogTitle>
            <DialogDescription>This field will appear on every {objectType} and in spreadsheet imports.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createField} className="space-y-4">
            <div className="space-y-2">
              <Label>Field name</Label>
              <Input value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} placeholder="e.g. Contract value" data-testid="new-property-label" />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={newField.field_type} onValueChange={(v) => setNewField({ ...newField, field_type: v })}>
                <SelectTrigger data-testid="new-property-type"><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {HAS_OPTIONS(newField.field_type) && (
              <div className="space-y-2">
                <Label>Options <span className="text-gray-400 font-normal">(comma-separated)</span></Label>
                <Input value={newField.options} onChange={(e) => setNewField({ ...newField, options: e.target.value })} placeholder="Option A, Option B, Option C" data-testid="new-property-options" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Description <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input value={newField.description} onChange={(e) => setNewField({ ...newField, description: e.target.value })} placeholder="What this field is for" data-testid="new-property-description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-new-property-btn">
                {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : 'Create property'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-white" data-testid="edit-property-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-violet-600" /> Edit property</DialogTitle>
            <DialogDescription>
              Type <strong>{TYPE_LABEL[editTarget?.field_type] || editTarget?.field_type}</strong> can't be changed. Existing values are preserved.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Field name</Label>
              <Input value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} data-testid="edit-property-label" />
            </div>
            {HAS_OPTIONS(editTarget?.field_type) && (
              <div className="space-y-2">
                <Label>Options <span className="text-gray-400 font-normal">(comma-separated)</span></Label>
                <Input value={editForm.options} onChange={(e) => setEditForm({ ...editForm, options: e.target.value })} data-testid="edit-property-options" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Description <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} data-testid="edit-property-description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
              <Button type="submit" disabled={savingEdit} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-edit-property-btn">
                {savingEdit ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Save changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Archive confirm */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent className="bg-white" data-testid="archive-property-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive "{archiveTarget?.label}"?</AlertDialogTitle>
            <AlertDialogDescription>
              It will be hidden from records and imports, but the values already saved on your {objectType}s are kept. You can restore it any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doArchive(); }} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-archive-btn">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomProperties;
